/**
 * Executive Summary Card - Business Value Calculator
 * Sprint 7: Day 3
 *
 * Displays 4 key financial metrics:
 * - NPV (Net Present Value): Total value in today's dollars
 * - IRR (Internal Rate of Return): Annualized return percentage
 * - ROI (Return on Investment): Total return percentage
 * - Payback Period: Months to break even
 *
 * Features:
 * - Color semantics (Green: positive, Red: negative, Blue: neutral)
 * - Trend arrows showing direction
 * - Metric interpretations (Excellent/Good/Fair/Poor/Critical)
 * - Input validation warnings if data incomplete
 */

import React from 'react';

export interface FinancialMetrics {
  npv: number;           // Net Present Value in USD
  irr: number;           // Internal Rate of Return in percentage
  roi: number;           // Return on Investment in percentage
  paybackMonths: number | null; // Payback period in months (null if never breaks even)
}

export interface ValidationWarning {
  severity: 'error' | 'warning' | 'info';
  message: string;
}

interface ExecutiveSummaryCardProps {
  metrics: FinancialMetrics | null;
  validationWarnings?: ValidationWarning[];
  isLoading?: boolean;
}

export const ExecutiveSummaryCard: React.FC<ExecutiveSummaryCardProps> = ({
  metrics,
  validationWarnings = [],
  isLoading = false
}) => {
  // Metric interpretation helpers
  const getNPVInterpretation = (npv: number): { label: string; color: string } => {
    if (npv >= 500000) return { label: 'Excellent', color: '#48bb78' };
    if (npv >= 100000) return { label: 'Good', color: '#68d391' };
    if (npv >= 0) return { label: 'Positive', color: '#9ae6b4' };
    if (npv >= -50000) return { label: 'Marginal', color: '#fc8181' };
    return { label: 'Negative', color: '#e53e3e' };
  };

  const getIRRInterpretation = (irr: number, discountRate: number = 10): { label: string; color: string } => {
    if (irr >= discountRate + 20) return { label: 'Excellent', color: '#48bb78' };
    if (irr >= discountRate + 10) return { label: 'Good', color: '#68d391' };
    if (irr >= discountRate) return { label: 'Acceptable', color: '#9ae6b4' };
    if (irr >= 0) return { label: 'Below Target', color: '#fc8181' };
    return { label: 'Negative', color: '#e53e3e' };
  };

  const getROIInterpretation = (roi: number): { label: string; color: string } => {
    if (roi >= 200) return { label: 'Excellent', color: '#48bb78' };
    if (roi >= 100) return { label: 'Good', color: '#68d391' };
    if (roi >= 50) return { label: 'Positive', color: '#9ae6b4' };
    if (roi >= 0) return { label: 'Marginal', color: '#fc8181' };
    return { label: 'Negative', color: '#e53e3e' };
  };

  const getPaybackInterpretation = (months: number): { label: string; color: string } => {
    if (months <= 12) return { label: 'Excellent', color: '#48bb78' };
    if (months <= 24) return { label: 'Good', color: '#68d391' };
    if (months <= 36) return { label: 'Acceptable', color: '#9ae6b4' };
    return { label: 'Long', color: '#fc8181' };
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="executive-summary-card">
        <div className="summary-loading">
          <div className="summary-spinner" role="status" aria-label="Calculating financial metrics" />
          <p className="summary-loading-text">Calculating financial metrics...</p>
        </div>
      </div>
    );
  }

  // Render validation warnings
  if (validationWarnings.length > 0) {
    return (
      <div className="executive-summary-card">
        <div className="summary-warnings" role="alert">
          <h3 className="summary-warnings-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            Input Validation Issues
          </h3>
          <ul className="summary-warnings-list">
            {validationWarnings.map((warning, index) => (
              <li key={index} className={`summary-warning-item summary-warning-${warning.severity}`}>
                {warning.message}
              </li>
            ))}
          </ul>
          <p className="summary-warnings-footer">
            Please review the Benefits and Costs tabs to ensure all required data is entered.
          </p>
        </div>
      </div>
    );
  }

  // Render metrics (if available)
  if (!metrics) {
    return (
      <div className="executive-summary-card">
        <div className="summary-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ margin: '0 auto 1rem', opacity: 0.3 }}>
            <line x1="18" y1="20" x2="18" y2="10" />
            <line x1="12" y1="20" x2="12" y2="4" />
            <line x1="6" y1="20" x2="6" y2="14" />
          </svg>
          <p className="summary-empty-text">Configure benefits and costs to see financial results</p>
        </div>
      </div>
    );
  }

  const npvInterp = getNPVInterpretation(metrics.npv);
  const irrInterp = getIRRInterpretation(metrics.irr);
  const roiInterp = getROIInterpretation(metrics.roi);
  const paybackInterp = metrics.paybackMonths !== null
    ? getPaybackInterpretation(metrics.paybackMonths)
    : { label: 'Never', color: '#e53e3e' };

  return (
    <div className="executive-summary-card">
      <h2 className="summary-title">Executive Summary</h2>
      <p className="summary-subtitle">
        Key financial metrics for your FAIR implementation business case
      </p>

      <div className="metrics-grid">
        {/* NPV Metric */}
        <div className="metric-card metric-card-primary" style={{ borderLeftColor: npvInterp.color }}>
          <div className="metric-header">
            <div className="metric-icon" style={{ background: npvInterp.color }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </div>
            <span className="metric-label">Net Present Value</span>
          </div>
          <div className="metric-value" style={{ color: '#000000' }}>
            ${metrics.npv >= 0 ? '' : '-'}${Math.abs(metrics.npv).toLocaleString()}
          </div>
          <div className="metric-interpretation" style={{ color: npvInterp.color }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {metrics.npv >= 0 ? (
                <polyline points="18 15 12 9 6 15" />
              ) : (
                <polyline points="6 9 12 15 18 9" />
              )}
            </svg>
            {npvInterp.label}
          </div>
          <p className="metric-description">
            Total value in today's dollars over 36 months
          </p>
        </div>

        {/* IRR Metric */}
        <div className="metric-card" style={{ borderLeftColor: irrInterp.color }}>
          <div className="metric-header">
            <div className="metric-icon" style={{ background: irrInterp.color }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
            </div>
            <span className="metric-label">Internal Rate of Return</span>
          </div>
          <div className="metric-value" style={{ color: '#000000' }}>
            {metrics.irr.toFixed(1)}%
          </div>
          <div className="metric-interpretation" style={{ color: irrInterp.color }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {metrics.irr >= 10 ? (
                <polyline points="18 15 12 9 6 15" />
              ) : (
                <polyline points="6 9 12 15 18 9" />
              )}
            </svg>
            {irrInterp.label}
          </div>
          <p className="metric-description">
            Annualized return rate (vs. 10% target)
          </p>
        </div>

        {/* ROI Metric */}
        <div className="metric-card" style={{ borderLeftColor: roiInterp.color }}>
          <div className="metric-header">
            <div className="metric-icon" style={{ background: roiInterp.color }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
              </svg>
            </div>
            <span className="metric-label">Return on Investment</span>
          </div>
          <div className="metric-value" style={{ color: '#000000' }}>
            {metrics.roi >= 0 ? '+' : ''}{metrics.roi.toFixed(0)}%
          </div>
          <div className="metric-interpretation" style={{ color: roiInterp.color }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {metrics.roi >= 0 ? (
                <polyline points="18 15 12 9 6 15" />
              ) : (
                <polyline points="6 9 12 15 18 9" />
              )}
            </svg>
            {roiInterp.label}
          </div>
          <p className="metric-description">
            Total return over 36 months
          </p>
        </div>

        {/* Payback Metric */}
        <div className="metric-card" style={{ borderLeftColor: paybackInterp.color }}>
          <div className="metric-header">
            <div className="metric-icon" style={{ background: paybackInterp.color }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <span className="metric-label">Payback Period</span>
          </div>
          <div className="metric-value" style={{ color: '#000000' }}>
            {metrics.paybackMonths !== null
              ? `${metrics.paybackMonths.toFixed(1)} mo`
              : 'Never'
            }
          </div>
          <div className="metric-interpretation" style={{ color: paybackInterp.color }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {metrics.paybackMonths !== null && metrics.paybackMonths <= 24 ? (
                <polyline points="18 15 12 9 6 15" />
              ) : (
                <polyline points="6 9 12 15 18 9" />
              )}
            </svg>
            {paybackInterp.label}
          </div>
          <p className="metric-description">
            Months to break even
          </p>
        </div>
      </div>

      <style>{`
        .executive-summary-card {
          background: #ffffff;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 2rem;
        }

        .summary-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #2d3748;
          margin-bottom: 0.5rem;
        }

        .summary-subtitle {
          font-size: 0.875rem;
          color: #718096;
          margin-bottom: 2rem;
        }

        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
        }

        .metric-card {
          padding: 1.5rem;
          background: #ffffff;
          border: 2px solid #e2e8f0;
          border-left-width: 4px;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .metric-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }

        .metric-card-primary {
          border-width: 3px;
        }

        .metric-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .metric-icon {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          color: #ffffff;
        }

        .metric-label {
          font-size: 0.75rem;
          font-weight: 700;
          color: #718096;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .metric-value {
          font-size: 2.5rem;
          font-weight: 800;
          line-height: 1;
          margin-bottom: 0.75rem;
        }

        .metric-interpretation {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          font-weight: 700;
          margin-bottom: 0.75rem;
        }

        .metric-description {
          font-size: 0.75rem;
          color: #a0aec0;
          line-height: 1.4;
        }

        /* Loading State */
        .summary-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
        }

        .summary-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #0094ab;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .summary-loading-text {
          margin-top: 1rem;
          font-size: 0.875rem;
          color: #718096;
        }

        /* Empty State */
        .summary-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          text-align: center;
        }

        .summary-empty-text {
          font-size: 0.875rem;
          color: #a0aec0;
        }

        /* Validation Warnings */
        .summary-warnings {
          padding: 1.5rem;
          background: #fff5f5;
          border: 2px solid #fc8181;
          border-radius: 8px;
        }

        .summary-warnings-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 1.125rem;
          font-weight: 700;
          color: #c53030;
          margin-bottom: 1rem;
        }

        .summary-warnings-list {
          list-style: none;
          padding: 0;
          margin: 0 0 1rem 0;
        }

        .summary-warning-item {
          padding: 0.75rem 1rem;
          margin-bottom: 0.5rem;
          border-radius: 6px;
          font-size: 0.875rem;
          line-height: 1.6;
        }

        .summary-warning-error {
          background: #fee;
          color: #c53030;
        }

        .summary-warning-warning {
          background: #fffaf0;
          color: #c05621;
        }

        .summary-warning-info {
          background: #ebf8ff;
          color: #2c5282;
        }

        .summary-warnings-footer {
          font-size: 0.875rem;
          color: #718096;
          margin: 0;
        }

        /* Responsive Design */
        @media (max-width: 968px) {
          .metrics-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .metric-value {
            font-size: 2rem;
          }
        }

        @media (max-width: 640px) {
          .metrics-grid {
            grid-template-columns: 1fr;
          }

          .metric-value {
            font-size: 1.75rem;
          }
        }
      `}</style>
    </div>
  );
};

export default ExecutiveSummaryCard;
